import { emptyRow, toYMD } from './snapshot_form_shared';

/**
 * Mirrors backend `run_update_snapshot_positions` for a single as-of date:
 * per tradeable asset, quantity = buy_qty − sell_qty, value = buy_net − sell_net
 * from operations with operation_date <= asOfDate; skip when quantity === 0.
 *
 * @param {string} asOfDate — snapshot date (YYYY-MM-DD or compatible)
 * @param {object[]} tradeableAssets — assets with id, asset_code, asset_name
 * @param {object[]} operations — items with asset_code, operation_date, operation_status, quantity, net_value
 * @returns {{ rows: object[] }}
 */
export function buildTradeablePositionRowsAsOf(asOfDate, tradeableAssets, operations) {
  const cutoff = toYMD(asOfDate);
  if (!cutoff) {
    return { rows: [] };
  }

  const opsByCode = new Map();
  for (const op of operations || []) {
    const code = op.asset_code != null ? String(op.asset_code).trim() : '';
    if (!code) continue;
    if (!opsByCode.has(code)) opsByCode.set(code, []);
    opsByCode.get(code).push(op);
  }

  const rows = [];
  for (const asset of tradeableAssets || []) {
    if (asset.id == null) continue;
    const code = asset.asset_code != null ? String(asset.asset_code).trim() : '';
    const ops = (opsByCode.get(code) || []).filter((op) => {
      const od = toYMD(op.operation_date);
      return od && od <= cutoff;
    });

    let buyQty = 0;
    let sellQty = 0;
    let buyNet = 0;
    let sellNet = 0;
    for (const op of ops) {
      const q = parseFloat(op.quantity) || 0;
      const nv = parseFloat(op.net_value) || 0;
      if (op.operation_status === 'buy') {
        buyQty += q;
        buyNet += nv;
      } else if (op.operation_status === 'sell') {
        sellQty += q;
        sellNet += nv;
      }
    }

    let quantity = buyQty - sellQty;
    let value = buyNet - sellNet;
    if (quantity === 0) {
      value = 0;
      continue;
    }

    rows.push({
      ...emptyRow(),
      asset: String(asset.id),
      snapshot_source: 'generated',
      snapshot_quantity: String(quantity),
      snapshot_value: String(Math.round(value * 100) / 100),
    });
  }

  const assetById = new Map(
    (tradeableAssets || []).map((x) => [String(x.id), x])
  );
  const sortKey = (row) => {
    const x = assetById.get(row.asset);
    return x?.asset_code != null ? String(x.asset_code).trim() : '';
  };
  rows.sort((a, b) =>
    sortKey(a).localeCompare(sortKey(b), undefined, { sensitivity: 'base' })
  );

  return { rows };
}
